import React from "react";

// Brand overlay presets (logo-color filters)
export const OVERLAY_HEX = {
  none: null,
  teal: "#1C4358",
  navy: "#1C4358",
  red: "#9C1E22",
  gold: "#E0A526",
  dark: "#0B1F2A",
  light: "#FFFFFF",
};

const isMp4 = (u) => /\.mp4(\?|$)/i.test(u || "");

/**
 * Renders a section background (none | solid color | image | video-URL) with an optional
 * brand-color overlay, and its children layered on top. Backward compatible:
 *  - When `bg` is unset/legacy, falls back to `fallbackImage` + the classic .mmm-hero-overlay-side.
 *  - Video backgrounds: direct .mp4 autoplays muted+looped (no sound); YouTube/Vimeo (or missing)
 *    gracefully fall back to the poster image so page speed stays safe.
 */
export const SectionBackground = ({
  bg,
  fallbackImage,
  fallbackAlt = "",
  children,
  className = "",
  innerClassName = "",
  testid,
}) => {
  const type = bg?.type || "none";
  const imageUrl =
    type === "color"
      ? null
      : type === "image"
      ? bg?.image_url || fallbackImage
      : type === "video"
      ? bg?.poster_url || fallbackImage
      : fallbackImage; // none / unset -> legacy fallback
  const mp4 = type === "video" && isMp4(bg?.video_url) ? bg.video_url : null;

  const overlaySet = typeof bg?.overlay === "string" && bg.overlay.length > 0;
  const overlayHex = overlaySet ? OVERLAY_HEX[bg.overlay] : null;
  const overlayOpacity = typeof bg?.overlay_opacity === "number" ? bg.overlay_opacity / 100 : 0.4;

  return (
    <div className={`relative isolate overflow-hidden ${className}`} data-testid={testid}>
      {type === "color" ? (
        <div className="absolute inset-0" style={{ backgroundColor: bg?.color || "#1C4358" }} />
      ) : imageUrl ? (
        <img src={imageUrl} alt={bg?.image_alt || fallbackAlt} className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-[#1C4358]" />
      )}
      {mp4 && (
        <video
          className="absolute inset-0 h-full w-full object-cover"
          src={mp4}
          poster={bg?.poster_url || fallbackImage}
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          aria-hidden="true"
        />
      )}
      {overlaySet ? (
        overlayHex && overlayOpacity > 0 ? (
          <div className="absolute inset-0" style={{ backgroundColor: overlayHex, opacity: overlayOpacity }} />
        ) : null
      ) : (
        <div className="absolute inset-0 mmm-hero-overlay-side" />
      )}
      <div className={`relative z-10 ${innerClassName}`}>{children}</div>
    </div>
  );
};

export default SectionBackground;
